import { useEffect, useState } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import { Link } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import BentoGrid from "@/components/BentoGrid";
import Catalog from "@/components/Catalog";
import JpgSlider from "@/components/JpgSlider";
import YoutubeSlider from "@/components/YoutubeSlider";
import ReviewsSection from "@/components/ReviewsSection";
import EnquirySection from "@/components/EnquirySection";
import ShiftSyncSection from "@/components/ShiftSyncSection";
import PlatformSection from "@/components/PlatformSection";
import BlogSection from "@/components/BlogSection";
import ChatWidget from "@/components/ChatWidget";
import WhatsAppButton from "@/components/WhatsAppButton";
import Footer from "@/components/Footer";
import CheckoutModal from "@/components/CheckoutModal";
import Seo from "@/components/Seo";

export default function Storefront({ autoOpenId = "" }) {
  const [products, setProducts] = useState([]);
  const [slides, setSlides] = useState([]);
  const [videos, setVideos] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [checkoutProduct, setCheckoutProduct] = useState(null);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/slider-images").then(({ data }) => setSlides(data)).catch(() => {});
    api.get("/youtube-videos").then(({ data }) => setVideos(data)).catch(() => {});
    api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (autoOpenId && products.length > 0 && !checkoutProduct) {
      const p = products.find((x) => x.id === autoOpenId);
      if (p) setCheckoutProduct(p);
    }
  }, [autoOpenId, products, checkoutProduct]);

  const featured = products.filter((p) => p.featured);
  const featuredList = (featured.length >= 3 ? featured : products).slice(0, 5);

  const handleBuy = (product, mode) => {
    if (mode === "preview") return;
    setCheckoutProduct(product);
  };

  // product detail state (/p/:id) — is product ka apna title/description/schema
  const detail = autoOpenId && checkoutProduct && checkoutProduct.id === autoOpenId ? checkoutProduct : null;
  const detailJsonLd = detail ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": detail.title,
    "description": (detail.description || detail.title).slice(0, 300),
    ...(detail.cover_image_url ? { "image": detail.cover_image_url } : {}),
    "offers": { "@type": "Offer", "priceCurrency": "INR", "price": String(detail.price),
      "availability": "https://schema.org/InStock", "url": `https://vooglyai.com/p/${detail.id}` },
  } : null;

  return (
    <div className="min-h-screen bg-cream text-slate-900" data-testid="storefront-page">
      <Seo
        title={detail ? `${detail.title.slice(0, 45)} — ₹${detail.price} | Voogly Education` : "Topper Notes, PDFs & Video Courses — Voogly Education"}
        description={detail ? `${(detail.description || detail.title).slice(0, 140)}... Buy now — instant PDF download, secure Razorpay & UPI payments.` : "Buy topper handwritten PDF notes, question banks & video courses for CBSE, JEE & NEET. Free MCQ practice portal, mock tests, instant download, secure Razorpay & UPI payments."}
        path={detail ? `/p/${detail.id}` : "/"}
        jsonLd={detailJsonLd}
      />
      <Header />
      <Hero settings={settings || {}} />
      <Marquee />
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-5 pt-10 sm:px-8">
          <Link to="/practice" data-testid="practice-banner-cta"
            className="group flex flex-col items-start justify-between gap-5 rounded-3xl bg-amber-500 p-7 sm:flex-row sm:items-center sm:p-9 card-lift">
            <span>
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] font-bold text-ink/70">Naya — Exam Practice Portal</span>
              <span className="mt-1 flex items-center gap-3 font-display text-xl font-extrabold text-ink sm:text-2xl">
                <GraduationCap size={26} /> Exam-wise MCQs · Level-wise practice · Har jawab ka rationale
              </span>
            </span>
            <span className="shrink-0 rounded-full bg-ink px-7 py-3.5 font-display font-bold text-amber-400 transition-transform group-hover:scale-105">
              Free Practice Karein →
            </span>
          </Link>
        </div>
      </section>
      <BentoGrid products={featuredList} onBuy={handleBuy} />
      <Catalog products={products} onBuy={handleBuy} />
      <JpgSlider images={slides} />
      <YoutubeSlider videos={videos} />
      <ShiftSyncSection />
      <PlatformSection />
      <BlogSection />
      <ReviewsSection reviews={reviews} />
      <EnquirySection />
      <Footer tagline={settings?.footer_tagline || ""} />
      <CheckoutModal product={checkoutProduct} open={!!checkoutProduct} onClose={() => setCheckoutProduct(null)} upiSettings={settings} />
      <ChatWidget />
      <WhatsAppButton supportMobile={settings?.support_mobile || ""} />
    </div>
  );
}
